import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	Output,
	ViewChild,
	OnInit,
	OnDestroy,
	OnChanges,
	SimpleChanges,
} from '@angular/core';
import {
	BehaviorSubject,
	interval,
	Observable,
	of,
	fromEvent,
	Subscription,
} from 'rxjs';
import { debounce, mergeMap, tap } from 'rxjs/operators';
import { emptyDevice, Device } from 'src/app/models/data/device';
import { ApiService, QueryParam } from 'src/app/services/api.services';
import {
	MatAutocompleteTrigger,
	MatAutocomplete,
} from '@angular/material/autocomplete';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatOption } from '@angular/material/select';
import { AsyncPipe } from '@angular/common';

export type SearchType = 'serialNumber' | 'propertyName' | 'deviceLocation';

@Component({
	selector: 'app-device-search-bar',
	templateUrl: './device-search-bar.component.html',
	styleUrls: ['./device-search-bar.component.scss'],
	imports: [
		MatFormField,
		MatInput,
		MatAutocompleteTrigger,
		MatIcon,
		MatPrefix,
		MatAutocomplete,
		MatOption,
		AsyncPipe,
	],
})
export class DeviceSearchBarComponent implements OnInit, OnDestroy, OnChanges {
	deviceLookupEvents: BehaviorSubject<string> = new BehaviorSubject<string>('');
	deviceSearchResults: Observable<any>;
	private scrollSubscription: Subscription | undefined;

	@Input() searchAllDevices = false;
	@Input() searchType: SearchType = 'serialNumber';
	@Input() externalSelectedDevice?: Device | null;

	@ViewChild('filterInput') public deviceInput!: ElementRef;
	@ViewChild(MatAutocompleteTrigger)
	autocompleteTrigger!: MatAutocompleteTrigger;

	@Output() selectedDeviceEvents = new EventEmitter<Device>();

	device: Device = emptyDevice();

	searchPlaceholder = $localize`:@@deviceSearchBar-searchPlaceholder:Search by`;
	searchByOptions: Record<SearchType, string> = {
		serialNumber: $localize`:@@deviceSearchBar-searchBySerialNumber:Serial Number`,
		propertyName: $localize`:@@deviceSearchBar-searchByPropertyName:Property Name`,
		deviceLocation: $localize`:@@deviceSearchBar-searchByDeviceLocation:Device Location`,
	};

	constructor(private readonly apiService: ApiService) {
		this.deviceSearchResults = this.deviceLookupEvents.asObservable().pipe(
			tap((it) => {
				if (it.length === 0) {
					this.device = emptyDevice();

					this.emitDevice();
				}
			}),
			debounce(() => interval(300)),
			mergeMap((searchTerm) => {
				if (searchTerm.length < 3) {
					return of([]);
				}

				const noResultsText =
					$localize`:@@deviceSearchBar-noResultsFoundFor:No results found for` +
					" '" +
					searchTerm +
					"' " +
					$localize`:@@deviceSearchBar-pleaseEnterDifferentSearchCriteria:please enter a different search criteria.`;

				const queryParameters: QueryParam[] = [
					{ param: 'searchType', value: this.searchType },
					{ param: 'searchTypeValue', value: searchTerm },
					{ param: 'rowIndex', value: 0 },
					{ param: 'rowCount', value: 100 },
					{ param: 'allowCodeGenerationBasedOnDeviceControl', value: true },
					{ param: 'allowCodeGenerationBasedOnStatus', value: true },
				];
				// if we want to let some users generate codes for all boxes in the system (not just ones their
				// company has access to) we'd need to have something here to handle that (new server call or
				// a parameter we can pass in to the existing call)
				return this.apiService
					.getAllDevices(queryParameters)
					.then((deviceDataList) => {
						if (deviceDataList.length === 0) {
							return [noResultsText];
						}

						return deviceDataList;
					});
			})
		);
	}

	ngOnInit() {
		// Subscribe to scroll events on window and close autocomplete
		this.scrollSubscription = fromEvent(window, 'scroll', {
			capture: true,
		}).subscribe(() => {
			if (this.autocompleteTrigger?.panelOpen) {
				this.autocompleteTrigger.closePanel();
			}
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['externalSelectedDevice']) {
			const device = changes['externalSelectedDevice'].currentValue;

			if (this.deviceInput) {
				if (device?.serialNumber) {
					// update UI without triggering search
					this.deviceInput.nativeElement.value = device.serialNumber;
				} else {
					// clear input
					this.deviceInput.nativeElement.value = '';
				}
			}
		}
	}

	ngOnDestroy() {
		if (this.scrollSubscription) {
			this.scrollSubscription.unsubscribe();
		}
	}

	onSearchTermChanged(event: Event): void {
		const input = event.target as HTMLInputElement;
		this.deviceLookupEvents.next(input.value);
	}

	displayDeviceSearchItemSerialNumber(item: any): string {
		return item?.device?.serialNumber ?? item;
	}

	displayFn(device: any): string {
		return device?.serialNumber ?? device?.device?.serialNumber ?? device ?? '';
	}

	displayDeviceSearchItemPropertyName(item: any): string {
		return (
			item?.device?.activeDeviceAssignment?.activeInstallation
				?.installationLocation?.location?.name ?? ''
		);
	}

	displayDeviceSearchItemDeviceLocation(item: any): string {
		return (
			item?.device?.activeDeviceAssignment?.activeInstallation
				?.installationLocation?.name ?? ''
		);
	}

	onDeviceSelected(deviceData: {
		device: Device;
		groupNames: string[];
		source: Components.Schemas.DeviceAccessSource;
	}): void {
		if (deviceData?.device?.serialNumber !== undefined) {
			this.device = deviceData.device;
			this.emitDevice();
		} else {
			// Clear the search if clicking on "No results" text
			if (this.deviceInput) {
				this.deviceInput.nativeElement.value = '';
				this.deviceLookupEvents.next('');
			}
		}
	}

	emitDevice(): void {
		this.selectedDeviceEvents.emit(this.device);
	}
}
